import { useState } from 'react';
import { Copy, Check, Loader2, Mail } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateInvite } from '@/hooks/use-invites';
import { toast } from '@/components/ui/toaster';
import type { Invite, GlobalRole } from '@/types';

type ApiErrorLike = { response?: { data?: { message?: string } } };

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteUserDialog({ open, onOpenChange }: InviteUserDialogProps) {
  const createInvite = useCreateInvite();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<GlobalRole>('user');
  const [emailError, setEmailError] = useState('');
  const [createdInvite, setCreatedInvite] = useState<Invite | null>(null);
  const [copied, setCopied] = useState(false);

  const resetForm = () => {
    setEmail('');
    setRole('user');
    setEmailError('');
    setCreatedInvite(null);
    setCopied(false);
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      resetForm();
    }
    onOpenChange(open);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setEmailError('Email is required');
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(email)) return;

    try {
      const invite = await createInvite.mutateAsync({
        email: email.trim(),
        role,
      });
      setCreatedInvite(invite);
      toast({ title: 'Invite created successfully' });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to create invite',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleCopyLink = async () => {
    if (!createdInvite) return;

    try {
      await navigator.clipboard.writeText(createdInvite.inviteLink);
      setCopied(true);
      toast({ title: 'Invite link copied to clipboard' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: 'Failed to copy',
        description: 'Please copy the link manually',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
          <DialogDescription>
            {createdInvite
              ? 'Share this invite link with the user to complete registration.'
              : 'Send an invitation to add a new user to your organization.'}
          </DialogDescription>
        </DialogHeader>

        {createdInvite ? (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted">
              <Mail className="h-5 w-5 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="font-medium truncate">{createdInvite.email}</p>
                <p className="text-sm text-muted-foreground capitalize">
                  Role: {createdInvite.role}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Invite Link</Label>
              <div className="flex gap-2">
                <Input
                  value={createdInvite.inviteLink}
                  readOnly
                  className="font-mono text-xs"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopyLink}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                This link expires in 7 days. Share it with the user to complete their registration.
              </p>
            </div>

            <DialogFooter className="pt-4">
              <Button variant="outline" onClick={() => handleClose(false)}>
                Close
              </Button>
              <Button onClick={() => setCreatedInvite(null)}>
                Invite Another
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) validateEmail(e.target.value);
                }}
                autoFocus
              />
              {emailError && <p className="text-xs text-destructive">{emailError}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={role} onValueChange={(v) => setRole(v as GlobalRole)}>
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <div className="flex flex-col">
                      <span>Admin</span>
                      <span className="text-xs text-muted-foreground">
                        Full system access and user management
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="supervisor">
                    <div className="flex flex-col">
                      <span>Supervisor</span>
                      <span className="text-xs text-muted-foreground">
                        Operational manager with team oversight
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="user">
                    <div className="flex flex-col">
                      <span>User</span>
                      <span className="text-xs text-muted-foreground">
                        Standard team member access
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="client">
                    <div className="flex flex-col">
                      <span>Client</span>
                      <span className="text-xs text-muted-foreground">
                        Limited access to assigned projects
                      </span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createInvite.isPending} className="gap-2">
                {createInvite.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {createInvite.isPending ? 'Creating...' : 'Create Invite'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
